import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const STORAGE_KEY = "stxoshi-show-usd";
const STXOSHI_USD_FALLBACK = 0.0000037; // used if the live STXOSHI price is missing

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [showUsd, setShowUsdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [prices, setPrices] = useState(null);

  const setShowUsd = useCallback((val) => {
    setShowUsdState((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const toggleUsd = useCallback(() => setShowUsd((v) => !v), [setShowUsd]);

  // Fetch + auto-refresh live USD prices (every 60s).
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch(`${API}/market/intelligence`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (alive && d?.prices) setPrices(d.prices);
        })
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(interval);
    };
  }, []);

  const usdRate = useCallback(
    (asset) => {
      const a = (asset || "stxoshi").toString().toLowerCase();
      const key = a === "ststx" ? "stx" : a;
      if (prices && prices[key]) return prices[key];
      if (a === "stxoshi") return STXOSHI_USD_FALLBACK;
      return null;
    },
    [prices]
  );

  const stxoshiUsd = usdRate("stxoshi");

  const value = { showUsd, setShowUsd, toggleUsd, prices, usdRate, stxoshiUsd };
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safe fallback if used outside the provider (keeps components from crashing).
    return {
      showUsd: false,
      setShowUsd: () => {},
      toggleUsd: () => {},
      prices: null,
      usdRate: (asset) => ((asset || "stxoshi").toLowerCase() === "stxoshi" ? STXOSHI_USD_FALLBACK : null),
      stxoshiUsd: STXOSHI_USD_FALLBACK,
    };
  }
  return ctx;
}
